import { BreakpointObserver } from "@angular/cdk/layout";
import { Component, inject, Input, OnInit } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ControlContainer, FormGroup, FormGroupDirective, ReactiveFormsModule } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import { ProjectService } from "@domain/project/project.service";
import { UserService } from "@domain/user/user.service";
import { filter, map, Observable } from "rxjs";
import { Breakpoints } from "../../../../../styles/breakpoints";

interface Option {
  value: string;
  label: string;
}

@Component({
  selector: "app-checklist-item-secondary-displays",
  templateUrl: "./checklist-item-secondary-displays.component.html",
  styleUrls: ["./checklist-item-secondary-displays.component.scss"],
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
  imports: [UiKitModule, ReactiveFormsModule],
  standalone: true,
})
export class ChecklistItemSecondaryDisplaysComponent implements OnInit {
  private breakpointObserver = inject(BreakpointObserver);

  protected isPhone = toSignal(this.breakpointObserver.observe(Breakpoints.mobile).pipe(map((result) => result.matches)), {
    initialValue: false,
  });

  private userService = inject(UserService);
  private projectService = inject(ProjectService);

  protected isReadOnlyUser$: Observable<boolean>;
  protected hasWritePermission$: Observable<boolean | undefined>;

  @Input()
  label!: string;

  @Input()
  subLabel!: string;

  @Input()
  groupName!: string;

  @Input()
  checklistOptions!: Record<string, Option[]>;

  @Input()
  notesPlaceholder: string = "";

  @Input()
  disabled: boolean = true;

  @Input()
  checklistForm!: FormGroup;

  options!: Option[];
  networkOptions!: Option[];
  connectionToOptions!: Option[];

  networkOptionsVisible = false;
  connectionToOptionsVisible = false;

  constructor() {
    this.isReadOnlyUser$ = this.userService.user$.pipe(map((user) => user.readOnlyAccess));
    this.hasWritePermission$ = this.projectService.hasUserWritePermission$.pipe(
      filter((val): val is boolean => val !== undefined),
    );
  }

  ngOnInit(): void {
    this.options = this.checklistOptions[this.groupName];
    this.networkOptions = this.checklistOptions["secondaryDisplaysNetwork"];
    this.connectionToOptions = this.checklistOptions["secondaryDisplaysConnectionTo"];

    this.networkOptionsVisible = this.isNetworkOptionsVisible(this.checklistForm.value);
    this.connectionToOptionsVisible = this.isConnectionToOptionsVisible(this.checklistForm.value);

    this.checklistForm.valueChanges.subscribe((value) => {
      this.networkOptionsVisible = this.isNetworkOptionsVisible(value);
      this.connectionToOptionsVisible = this.isConnectionToOptionsVisible(value);
    });
  }

  private isNetworkOptionsVisible(value: any) {
    const option = value[this.groupName].choices;

    if (!option) {
      return false;
    }

    return Object.values(option).some((option) => option === true);
  }

  private isConnectionToOptionsVisible(value: any) {
    return this.networkOptionsVisible && !!value[this.groupName].networkChoice;
  }
}
